// The Shipping writes (spec §3.8, §3.13): Schedule pickup, Add a line, edits on the schedule,
// taking a row off its pickup, and Mark shipped. Through the Worklist's writer (`writer.ts`): the
// same versions, one line's writes one after another, the progress toast and the stop-part-way
// message (OUTSIDE-PLATFORM: P4 no transaction). Each action's System log entry is the line's
// (or the pickup's); the records it makes for it are `partOf` it.
//
// Nothing is ever deleted: a row taken off is Removed, a pickup left empty Cancelled, a shipped
// one Shipped. A line nobody saved yet is saved by the first action that needs its id.

import {
  PICKUPS,
  ROUTING_LINES,
  SHIPMENTS,
  SHIPPING_ROWS,
  approvalPlan,
  carrierLogText,
  carryInput,
  diffFields,
  fieldChange,
  formatShortDay,
  pickupText,
  scheduledNote,
  rescheduleChanges,
  lineRef,
} from "@app/shared";
import type {
  LineView,
  PickupRecord,
  RescheduleField,
  RescheduleValues,
  RoutingLineRecord,
  ScheduleRow,
  ShipmentRecord,
  ShippingRowRecord,
} from "@app/shared";
import { SHIPPING_TOAST_MS, notify } from "./message";
import type { Writer } from "./writer";
import { lineText } from "./writer";

/**
 * A pickup's typed fields (Schedule pickup's "New pickup", Add a line). The carrier is picked from
 * A2000's or Deposco's list (item 8): its name, code and source; all empty for none.
 */
export type PickupInput = Pick<PickupRecord, "pickupDate" | "appTime" | "carrier" | "door" | "loadRef"> & {
  carrierCode: string;
  carrierSource: string;
  notes?: string;
};

/** What Mark shipped asks (spec §3.8, Figure 23). Loader, trailer and door only off the schedule. */
export interface ShipInput {
  shipDate: string;
  carrier: string;
  /** The carrier's code and source, as picked (item 8); "" for none, or the pickup's typed carrier. */
  carrierCode: string;
  carrierSource: string;
  loadRef: string;
  tracking: string;
  loader: string;
  trailer: string;
  door: string;
}

export const PICKUP_FIELDS = ["pickupDate", "appTime", "carrier", "door", "loadRef", "notes"] as const;
export type PickupField = (typeof PICKUP_FIELDS)[number];
export const ROW_FIELDS = ["ctns", "plts", "loader", "inTime", "outTime", "trailer", "wmsAsnTrip", "notes"] as const;
export type RowField = (typeof ROW_FIELDS)[number];

/** "10/16 · FedEx Freight · LD# 50001": a pickup in a few words (`shipping.ts`). */
export { pickupText };

const pickupRecordInput = (p: PickupInput, wh: string, status: PickupRecord["status"] = "Scheduled") => ({
  wh,
  pickupDate: p.pickupDate,
  appTime: p.appTime.trim(),
  carrier: p.carrier.trim(),
  carrierCode: p.carrierCode.trim() || null,
  carrierSource: (p.carrierSource.trim() || null) as PickupRecord["carrierSource"],
  door: p.door.trim(),
  loadRef: p.loadRef.trim(),
  notes: (p.notes ?? "").trim(),
  status,
});

/** A Reschedule's change with an empty carrier code or source saved as none, as the other writes do. */
const nullCarrier = (change: Partial<RescheduleValues>) => ({
  ...change,
  ...(change.carrierCode !== undefined ? { carrierCode: change.carrierCode || null } : {}),
  ...(change.carrierSource !== undefined ? { carrierSource: change.carrierSource || null } : {}),
});

export function useShippingWriter(writer: Writer) {
  const { put, create, ensureStored, chained, keyOf, batch, holding, copyOf, writeCopies, keep, patch, who } =
    writer.kit;
  const s = (n: number) => (n === 1 ? "" : "s");

  /** The line's stored id, saving it first when nobody had: part of the action's entry. */
  const idOf = async (line: LineView, action: string) => (await ensureStored(line, {}, { partOf: action })).id;

  /** A line's SCHEDULED SHIP DATE, set or cleared by the schedule; its entry is the action's. */
  const setSchedShip = async (line: LineView, id: string, value: string, entry: Parameters<typeof put>[3]) => {
    const change: Partial<RoutingLineRecord> = {
      ...(line.carry.sources.length ? carryInput(line) : {}),
      schedShip: value,
    };
    keep([await put(ROUTING_LINES, id, change, entry)]);
  };

  /** Each line onto `pickup` (one shipping row each) and its SCHEDULED SHIP DATE to the pickup's date. */
  const placeOn = (pickup: PickupRecord, lines: readonly LineView[], first: number, action: "schedule" | "add-line") =>
    lines.map((line, i) => ({
      label: `${line.customerName} · ${lineRef(line)}`,
      key: keyOf(line),
      run: () =>
        chained(keyOf(line), async () => {
          const id = await idOf(line, action);
          const row: Omit<ShippingRowRecord, "id" | "created_at" | "updated_at"> = {
            pickupId: pickup.id,
            lineId: id,
            wmsNo: line.wmsNo,
            ctns: null,
            plts: null,
            loader: "",
            inTime: "",
            outTime: "",
            trailer: "",
            wmsAsnTrip: line.wmsNo,
            notes: "",
            sortOrder: first + i,
            status: "On pickup",
          };
          await create(SHIPPING_ROWS, row, { partOf: `${action} ${id}` });
          await setSchedShip(line, id, pickup.pickupDate ?? "", {
            action,
            lineId: id,
            lineText: lineText(line),
            changes: fieldChange("schedShip", line.fields.schedShip, pickup.pickupDate),
            note: scheduledNote(pickup),
          });
        }),
    }));

  const newPickup = async (input: PickupInput, wh: string) => {
    const record = pickupRecordInput(input, wh);
    return (await create(PICKUPS, record, {
      action: "pickup",
      // "Carrier PERFORMANCE TEAM (PERF)": the code said with the name.
      changes: diffFields({}, { ...record, carrier: carrierLogText(record) }, [...PICKUP_FIELDS, "status"]),
      note: `New pickup ${pickupText(input)}`,
    })) as unknown as PickupRecord;
  };

  /**
   * Lines off their pickups (item 8: an Appointment date cleared, or Undo of one that scheduled
   * them): each row Removed, the line back on the Worklist with no SCHEDULED SHIP DATE, and a pickup
   * Cancelled when no row is left on it — as Remove on the Shipping page.
   */
  const takeOffNow = async (lines: readonly LineView[], rowsOf: (pickupId: string) => readonly { id: string }[]) => {
    const on = lines.filter((l) => l.onPickup);
    if (!on.length) return true;
    const off = new Set(on.map((l) => l.onPickup!.row.id));
    const ok = await batch(
      "Removing",
      on.map((l) => {
        const { row, pickup } = l.onPickup!;
        return {
          label: `${l.customerName} · ${lineRef(l)}`,
          key: keyOf(l),
          run: () =>
            chained(keyOf(l), async () => {
              await put(SHIPPING_ROWS, row.id, { status: "Removed" }, { partOf: `remove ${row.lineId}` });
              await setSchedShip(l, row.lineId, "", {
                action: "remove",
                lineId: row.lineId,
                lineText: lineText(l),
                changes: fieldChange("schedShip", l.fields.schedShip, ""),
                note: `Taken off the pickup ${pickupText(pickup)}`,
              });
            }),
        };
      }),
      6,
      "lines",
    );
    if (!ok) return false;
    const pickups = [...new Map(on.map((l) => [l.onPickup!.pickup.id, l.onPickup!.pickup])).values()];
    const empty = pickups.filter((p) => rowsOf(p.id).every((r) => off.has(r.id)));
    return batch(
      "Removing",
      empty.map((p) => ({
        label: `Pickup ${pickupText(p)}`,
        run: () =>
          put(
            PICKUPS,
            p.id,
            { status: "Cancelled" },
            {
              action: "pickup" as const,
              changes: fieldChange("status", p.status, "Cancelled"),
              note: `Pickup ${pickupText(p)} cancelled: no line left on it`,
            },
          ),
      })),
    );
  };

  /** Reschedule's saves: each pickup, then the lines whose date moved. */
  const reschedulePlans = async (
    plans: readonly { pickup: PickupRecord; change: Partial<RescheduleValues>; rows: readonly ScheduleRow[] }[],
  ): Promise<boolean> => {
    const ok = await batch(
      "Rescheduling",
      plans.map(({ pickup, change, rows }) => ({
        label: `Pickup ${pickupText(pickup)}`,
        keys: rows.map((r) => keyOf(r.line)),
        run: () =>
          chained(`pickup/${pickup.id}`, () =>
            put(PICKUPS, pickup.id, nullCarrier(change), {
              action: "pickup",
              changes: diffFields(
                { ...pickup, carrier: carrierLogText(pickup) },
                { ...pickup, ...change, carrier: carrierLogText({ ...pickup, ...change }) },
                [...PICKUP_FIELDS],
              ),
              note: `Pickup ${pickup.pickupDate ? formatShortDay(pickup.pickupDate) : pickup.id} · ${rows.length} line${s(rows.length)}`,
            }),
          ),
      })),
    );
    if (!ok) return false;
    const moved = plans.filter((x) => x.change.pickupDate !== undefined);
    const done = await batch(
      "Moving",
      moved.flatMap(({ pickup, change, rows }) =>
        rows.map((r) => ({
          label: `${r.line.customerName} · ${lineRef(r.line)}`,
          key: keyOf(r.line),
          run: () =>
            chained(keyOf(r.line), () =>
              setSchedShip(r.line, r.row.lineId, change.pickupDate!, {
                action: "schedule",
                lineId: r.row.lineId,
                lineText: lineText(r.line),
                changes: fieldChange("schedShip", r.line.fields.schedShip, change.pickupDate),
                note: [
                  `Pickup moved to ${formatShortDay(change.pickupDate!)}`,
                  change.carrier ?? pickup.carrier,
                  change.loadRef ?? pickup.loadRef,
                ]
                  .filter(Boolean)
                  .join(" · "),
              }),
            ),
        })),
      ),
      6,
      "lines",
    );
    if (done)
      notify(
        `Rescheduled ${plans.length === 1 ? "the pickup" : `${plans.length} pickups`}`,
        undefined,
        SHIPPING_TOAST_MS,
      );
    return done;
  };

  /** Moving some lines to a new pickup: the pickup, the rows, then the old pickups left empty. */
  const moveLinesNow = async (
    rows: readonly ScheduleRow[],
    typed: RescheduleValues,
    rowsOf: (pickupId: string) => readonly ScheduleRow[],
  ): Promise<boolean> => {
    let pickup: PickupRecord | undefined;
    const keys = rows.map((r) => keyOf(r.line));
    const made = await batch("Rescheduling", [
      {
        label: `New pickup ${pickupText(typed)}`,
        keys,
        run: async () => {
          pickup = await newPickup(typed, rows[0]?.pickup.wh ?? rows[0]?.line.wh ?? "");
        },
      },
    ]);
    if (!made || !pickup) return false;
    const to = pickup;
    const ok = await batch(
      "Moving",
      rows.map((r, i) => ({
        label: `${r.line.customerName} · ${lineRef(r.line)}`,
        key: keyOf(r.line),
        run: () =>
          chained(keyOf(r.line), async () => {
            // Its typed values go with it: CTNS, PLTS, Loader, In, Out, Trailer#, WMS / ASN / TRIP, Notes.
            const row: Omit<ShippingRowRecord, "id" | "created_at" | "updated_at"> = {
              pickupId: to.id,
              lineId: r.row.lineId,
              wmsNo: r.row.wmsNo,
              ctns: r.row.ctns,
              plts: r.row.plts,
              loader: r.row.loader,
              inTime: r.row.inTime,
              outTime: r.row.outTime,
              trailer: r.row.trailer,
              wmsAsnTrip: r.row.wmsAsnTrip,
              notes: r.row.notes,
              sortOrder: i,
              status: "On pickup",
            };
            await put(SHIPPING_ROWS, r.row.id, { status: "Removed" }, { partOf: `schedule ${r.row.lineId}` });
            await create(SHIPPING_ROWS, row, { partOf: `schedule ${r.row.lineId}` });
            await setSchedShip(r.line, r.row.lineId, to.pickupDate ?? "", {
              action: "schedule",
              lineId: r.row.lineId,
              lineText: lineText(r.line),
              changes: fieldChange("schedShip", r.line.fields.schedShip, to.pickupDate),
              note: scheduledNote(to, r.pickup),
            });
          }),
      })),
      6,
      "lines",
    );
    if (!ok) return false;
    // An old pickup with none of its rows left on it is Cancelled.
    const moved = new Set(rows.map((r) => r.row.id));
    const left = [...new Map(rows.map((r) => [r.pickup.id, r.pickup])).values()].filter((p) =>
      rowsOf(p.id).every((r) => moved.has(r.row.id)),
    );
    const closed = await batch(
      "Rescheduling",
      left.map((p) => ({
        label: `Pickup ${pickupText(p)}`,
        run: () =>
          put(
            PICKUPS,
            p.id,
            { status: "Cancelled" },
            {
              action: "pickup" as const,
              changes: fieldChange("status", p.status, "Cancelled"),
              note: `Pickup ${pickupText(p)} cancelled: no line left on it`,
            },
          ),
      })),
    );
    notify(`${rows.length} line${s(rows.length)} on the new pickup ${pickupText(to)}`, undefined, SHIPPING_TOAST_MS);
    return closed;
  };

  return {
    /**
     * Schedule pickup (spec §3.8): a new pickup, or lines added to one already scheduled. The
     * checks are the dialog's (`checkPickup`), before this.
     */
    async schedule(
      lines: readonly LineView[],
      target: { input: PickupInput } | { pickup: PickupRecord; rows: number },
      action: "schedule" | "add-line" = "schedule",
      /** From the Appointment date cell (item 8): Ctrl+Z takes the lines off the pickup again. */
      undoable = false,
    ): Promise<boolean> {
      // The lines show "saving" from the dialog closing, through the new pickup, to their rows (item 1).
      return holding(
        lines.map((l) => keyOf(l)),
        async () => {
          let pickup: PickupRecord | undefined;
          const made = await batch("Scheduling", [
            {
              label: "input" in target ? `New pickup ${pickupText(target.input)}` : `Pickup ${target.pickup.id}`,
              run: async () => {
                pickup = "input" in target ? await newPickup(target.input, lines[0]?.wh ?? "") : target.pickup;
              },
            },
          ]);
          if (!made || !pickup) return false;
          const ok = await batch(
            "Scheduling",
            placeOn(pickup, lines, "rows" in target ? target.rows : 0, action),
            6,
            "lines",
          );
          if (ok)
            notify(
              `${lines.length} line${s(lines.length)} on the pickup ${pickupText(pickup)}`,
              "They are on the Shipping schedule now.",
              SHIPPING_TOAST_MS,
            );
          if (ok && undoable) {
            const keys = new Set(lines.map((l) => keyOf(l)));
            const placed = pickup.id;
            writer.pushUndo((now) => {
              const rowsOn = (id: string) =>
                now.filter((l) => l.onPickup?.pickup.id === id).map((l) => l.onPickup!.row);
              return takeOffNow(
                now.filter((l) => l.onPickup?.pickup.id === placed && keys.has(keyOf(l))),
                rowsOn,
              );
            });
          }
          return ok;
        },
      );
    },

    /**
     * One value for the whole pickup (Date, PICK UP #, Carrier, App Time, Door): the pickup's, so
     * every row shows it. A new date moves every line's SCHEDULED SHIP DATE with it.
     */
    /** The pickup's carrier, picked from the source list (item 8): name, code and source in one save. */
    editCarrier(
      pickup: PickupRecord,
      pick: { carrier: string; carrierCode: string; carrierSource: string } | null,
      rows: readonly ScheduleRow[],
    ) {
      const change = {
        carrier: pick?.carrier ?? "",
        carrierCode: pick?.carrierCode || null,
        carrierSource: (pick?.carrierSource || null) as PickupRecord["carrierSource"],
      };
      patch((p) => ({ ...p, pickups: p.pickups.map((x) => (x.id === pickup.id ? { ...x, ...change } : x)) }));
      return batch("Saving", [
        {
          label: `Pickup ${pickupText(pickup)}`,
          run: () =>
            chained(`pickup/${pickup.id}`, () =>
              put(PICKUPS, pickup.id, change, {
                action: "pickup",
                changes: fieldChange("carrier", carrierLogText(pickup), carrierLogText(change)),
                note: `Pickup ${pickup.pickupDate ? formatShortDay(pickup.pickupDate) : pickup.id} · ${rows.length} line${s(rows.length)}`,
              }),
            ),
        },
      ]);
    },

    /** Lines off their pickups (an Appointment date cleared): as Remove, several at once. */
    takeOff(lines: readonly LineView[], rowsOf: (pickupId: string) => readonly ScheduleRow[]) {
      return holding(
        lines.map((l) => keyOf(l)),
        async () => {
          const ok = await takeOffNow(lines, (id) => rowsOf(id).map((r) => r.row));
          if (ok)
            notify(
              "Taken off the pickup",
              `${lines.length} line${s(lines.length)} back on the Worklist with no scheduled ship date.`,
              SHIPPING_TOAST_MS,
            );
          return ok;
        },
      );
    },

    async editPickup(
      pickup: PickupRecord,
      field: PickupField,
      value: string,
      rows: readonly ScheduleRow[],
      action: "pickup" | "restore" = "pickup",
    ) {
      patch((p) => ({ ...p, pickups: p.pickups.map((x) => (x.id === pickup.id ? { ...x, [field]: value } : x)) }));
      const ok = await batch("Saving", [
        {
          label: `Pickup ${pickupText(pickup)}`,
          // A pickup's saves one after another too (Date, then Carrier, quickly).
          run: () =>
            chained(`pickup/${pickup.id}`, () =>
              put(
                PICKUPS,
                pickup.id,
                { [field]: value },
                {
                  action,
                  changes: fieldChange(field, pickup[field], value),
                  // "Pickup 10/26 · 2 lines": the System log adds the field, old → new (`changeText`).
                  note: `Pickup ${pickup.pickupDate ? formatShortDay(pickup.pickupDate) : pickup.id} · ${rows.length} line${s(rows.length)}`,
                },
              ),
            ),
        },
      ]);
      if (!ok || field !== "pickupDate") return ok;
      return batch(
        "Moving",
        rows.map((r) => ({
          label: `${r.line.customerName} · ${lineRef(r.line)}`,
          key: keyOf(r.line),
          run: () =>
            chained(keyOf(r.line), () =>
              setSchedShip(r.line, r.row.lineId, value, {
                action: "schedule",
                lineId: r.row.lineId,
                lineText: lineText(r.line),
                changes: fieldChange("schedShip", r.line.fields.schedShip, value),
                note: [`Pickup moved to ${formatShortDay(value)}`, pickup.carrier, pickup.loadRef]
                  .filter(Boolean)
                  .join(" · "),
              }),
            ),
        })),
        6,
        "lines",
      );
    },

    /**
     * Reschedule (item 2) of whole pickups: each pickup's fields in one save — the System log's
     * change of the pickup, old → new, which Restore puts back — and, when its date moves, every
     * line's SCHEDULED SHIP DATE with it.
     */
    async reschedule(
      pickups: readonly PickupRecord[],
      typed: RescheduleValues,
      several: readonly RescheduleField[],
      rowsOf: (pickupId: string) => readonly ScheduleRow[],
    ): Promise<boolean> {
      const plans = pickups
        .map((pickup) => ({ pickup, change: rescheduleChanges(pickup, typed, several), rows: rowsOf(pickup.id) }))
        .filter((x) => Object.keys(x.change).length);
      if (!plans.length) {
        notify("Nothing to change", "The pickup already has these values.", SHIPPING_TOAST_MS);
        return true;
      }
      return holding(
        plans.flatMap((x) => x.rows.map((r) => keyOf(r.line))),
        () => reschedulePlans(plans),
      );
    },

    /**
     * Reschedule (item 2) of some lines of a pickup: a new pickup with the typed values, those
     * lines' rows moved to it (each row Removed from its old pickup, a new one on the new pickup
     * with its typed values), their SCHEDULED SHIP DATE the new date; an old pickup left empty is
     * Cancelled, as today.
     */
    moveLines(
      rows: readonly ScheduleRow[],
      typed: RescheduleValues,
      rowsOf: (pickupId: string) => readonly ScheduleRow[],
    ): Promise<boolean> {
      return holding(
        rows.map((r) => keyOf(r.line)),
        () => moveLinesNow(rows, typed, rowsOf),
      );
    },
    /** A field typed per row (CTNS, PLTS, Loader, In, Out, Trailer#, WMS / ASN / TRIP, Notes). */
    editRow(r: ScheduleRow, field: RowField, value: string | number | null, action: "edit" | "restore" = "edit") {
      // Shown at once, so the next cell's checks (Out after In) see it; a failure reloads the page.
      patch((p) => ({
        ...p,
        shippingRows: p.shippingRows.map((x) => (x.id === r.row.id ? { ...x, [field]: value } : x)),
      }));
      const was = r.row[field];
      return batch("Saving", [
        {
          label: `${r.line.customerName} · ${lineRef(r.line)}`,
          // One row's saves one after another (a quick Tab across its cells): each over the last's version.
          run: () =>
            chained(`shipping-row/${r.row.id}`, () =>
              put(
                SHIPPING_ROWS,
                r.row.id,
                { [field]: value },
                {
                  action,
                  lineId: r.row.lineId,
                  lineText: lineText(r.line),
                  changes: fieldChange(field, was, value),
                  note: `Shipping schedule · pickup ${pickupText(r.pickup)}`,
                },
              ),
            ),
        },
      ]);
    },

    /**
     * Off its pickup: the row Removed, the line back on the Worklist with no SCHEDULED SHIP DATE,
     * and the pickup Cancelled when no row is left on it.
     */
    async remove(r: ScheduleRow, others: readonly ScheduleRow[]) {
      const ok = await batch(
        "Removing",
        [
          {
            label: `${r.line.customerName} · ${lineRef(r.line)}`,
            key: keyOf(r.line),
            run: () =>
              chained(keyOf(r.line), async () => {
                await put(SHIPPING_ROWS, r.row.id, { status: "Removed" }, { partOf: `remove ${r.row.lineId}` });
                await setSchedShip(r.line, r.row.lineId, "", {
                  action: "remove",
                  lineId: r.row.lineId,
                  lineText: lineText(r.line),
                  changes: fieldChange("schedShip", r.line.fields.schedShip, ""),
                  note: `Taken off the pickup ${pickupText(r.pickup)}`,
                });
              }),
          },
          ...(others.length
            ? []
            : [
                {
                  label: `Pickup ${pickupText(r.pickup)}`,
                  run: () =>
                    put(
                      PICKUPS,
                      r.pickup.id,
                      { status: "Cancelled" },
                      {
                        action: "pickup" as const,
                        changes: fieldChange("status", r.pickup.status, "Cancelled"),
                        note: `Pickup ${pickupText(r.pickup)} cancelled: no line left on it`,
                      },
                    ),
                },
              ]),
        ],
        1,
      );
      if (ok)
        notify(
          "Taken off the pickup",
          "The line is back on the Worklist with no scheduled ship date.",
          SHIPPING_TOAST_MS,
        );
      return ok;
    },

    /**
     * Mark shipped (spec §3.8, §3.13): a shipment per line, the line finished as Shipped (it leaves
     * the Worklist for History, with its pick tickets as its baseline), its row Shipped, and each
     * pickup Shipped once none of its rows is left.
     */
    async ship(
      lines: readonly LineView[],
      input: ShipInput,
      /** Every row still on each pickup, for "the pickup is shipped once all of them are". */
      rowsOf: (pickupId: string) => readonly ScheduleRow[],
    ): Promise<boolean> {
      const jobs = lines.map((line) => ({
        label: `${line.customerName} · ${lineRef(line)}`,
        key: keyOf(line),
        run: () =>
          chained(keyOf(line), async () => {
            const id = await idOf(line, "ship");
            const on = line.onPickup;
            const shipment: Omit<ShipmentRecord, "id" | "created_at" | "updated_at"> = {
              lineId: id,
              pickupId: on?.pickup.id ?? "",
              shipDate: input.shipDate,
              carrier: input.carrier.trim(),
              carrierCode: input.carrierCode.trim() || null,
              carrierSource: (input.carrierSource.trim() || null) as ShipmentRecord["carrierSource"],
              loadRef: input.loadRef.trim(),
              tracking: input.tracking.trim(),
              loader: on ? on.row.loader : input.loader.trim(),
              trailer: on ? on.row.trailer : input.trailer.trim(),
              door: on ? on.pickup.door : input.door.trim(),
              notes: on?.row.notes ?? "",
              ctns: on?.row.ctns ?? null,
              // The line's one pallets value (017), copied as it ships.
              plts: line.fields.pallets === "" ? null : Number(line.fields.pallets),
              inTime: on?.row.inTime ?? "",
              outTime: on?.row.outTime ?? "",
            };
            await create(SHIPMENTS, shipment, { partOf: `ship ${id}` });
            if (on) await put(SHIPPING_ROWS, on.row.id, { status: "Shipped" }, { partOf: `ship ${id}` });
            // Its pick tickets stay with it: A2000 may still list them as open for a while.
            const plan = approvalPlan(line, id, copyOf);
            const change: Partial<RoutingLineRecord> = {
              ...(line.carry.sources.length ? carryInput(line) : {}),
              ...plan.fields,
              finishedStatus: "Shipped",
              finishedAt: input.shipDate,
              finishedBy: who,
              finishedNote: "",
            };
            keep([
              await put(ROUTING_LINES, id, change, {
                action: "ship",
                lineId: id,
                lineText: lineText(line),
                changes: fieldChange("finishedStatus", "", "Shipped"),
                note: `Marked shipped ${formatShortDay(input.shipDate)} · ${[carrierLogText(input), input.loadRef, input.tracking && `tracking ${input.tracking}`].filter(Boolean).join(" · ")}`,
              }),
            ]);
            await writeCopies(plan.writes);
          }),
      }));
      const ok = await batch("Shipping", jobs, 6, "lines");
      if (!ok) return false;
      // A pickup whose rows are all shipped now is Shipped.
      const shippedNow = new Set(lines.map((l) => l.onPickup?.row.id).filter(Boolean));
      const pickups = [
        ...new Map(lines.flatMap((l) => (l.onPickup ? [[l.onPickup.pickup.id, l.onPickup.pickup]] : []))).values(),
      ];
      const done = pickups.filter((p) => rowsOf(p.id).every((r) => shippedNow.has(r.row.id)));
      const closed = await batch(
        "Shipping",
        done.map((p) => ({
          label: `Pickup ${pickupText(p)}`,
          run: () =>
            put(
              PICKUPS,
              p.id,
              { status: "Shipped" },
              {
                action: "pickup" as const,
                changes: fieldChange("status", p.status, "Shipped"),
                note: `Pickup ${pickupText(p)} shipped: every line on it shipped`,
              },
            ),
        })),
      );
      notify(
        `${lines.length} line${s(lines.length)} shipped`,
        "They left the Worklist and are in the Ship log and History.",
        SHIPPING_TOAST_MS,
      );
      return closed;
    },
  };
}

export type ShippingWriter = ReturnType<typeof useShippingWriter>;
